import React, { useEffect, useRef, useState } from 'react';
import './service.css';
import MobileNav from '../common/MobileNav';
import { ThemeContext } from "../common/ThemeContext";


const Services = () => {
  const [activeModal, setActiveModal] = useState(null);
  const heroRef = useRef(null);

  const [scrollProgress, setScrollProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
  let ticking = false;
  
  const handleScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        // Parallax effect
        if (heroRef.current) {
          const scrolled = window.pageYOffset;
          heroRef.current.style.transform = `translateY(${scrolled * 0.4}px)`;
        }

        // Scroll progress
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const scrollTop = window.pageYOffset;
        const progress = (scrollTop / (documentHeight - windowHeight)) * 100;
        setScrollProgress(progress);

        // Back to top button
        setShowBackToTop(scrollTop > 300);

        ticking = false;
      });
      ticking = true;
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  return () => window.removeEventListener('scroll', handleScroll);
}, []);

  useEffect(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    },
    { threshold: 0.2 }
  );

  const elements = document.querySelectorAll('.stat-card, .service-page-card, .modal-service-card');
  elements.forEach((el) => observer.observe(el));

  return () => observer.disconnect();
}, [activeModal]);

  const stats = [
    {
      value: '3',
      label: 'Success in getting happy customer',
      icon: '😊'
    },
    {
      value: '5',
      label: 'Successful business',
      icon: '🚀'
    },
    {
      value: '6',
      label: 'Total clients who love Elexyt',
      icon: '❤️'
    },
    {
      value: '4',
      label: 'Stars reviews given by satisfied clients',
      icon: '⭐'
    }
  ];

  const services = [
    {
      id: 'webDesign',
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="16 18 22 12 16 6" />
          <polyline points="8 6 2 12 8 18" />
        </svg>
      ),
      title: 'Web Design',
      tag: 'Design',
      description: 'Elexyt design and development practices ensure that websites provide an optimal viewing experience across a range of devices, from desktops to smartphones and tablets.',
      modalContent: {
        title: 'Web Design Services',
        tagline: 'Crafting Beautiful and Functional Websites',
        intro: 'At Elexyt Technologies, we specialize in designing websites that not only look stunning but also provide a seamless user experience. Our web design services are tailored to meet the unique needs of your business, ensuring that your online presence stands out in the digital landscape.',
        offerings: [
          { title: 'Custom Website Design', description: 'We create bespoke websites that align with your brand identity and business goals.' },
          { title: 'Responsive Design', description: 'Our designs are optimized for all devices, ensuring your site looks great on desktops, tablets, and smartphones.' },
          { title: 'UI/UX Design', description: 'We focus on creating intuitive and user-friendly interfaces that enhance user engagement.' },
          { title: 'eCommerce Solutions', description: 'We design and develop eCommerce websites that drive conversions and provide a smooth shopping experience.' },
          { title: 'Website Redesign', description: 'Give your existing website a fresh and modern look with our redesign services.' }
        ],
        whyChoose: [
          { title: 'Experienced Designers', description: 'Our team has years of experience in web design and a deep understanding of the latest trends and technologies.' },
          { title: 'Creative Approach', description: 'We blend creativity with functionality to create websites that are both beautiful and effective.' },
          { title: 'Client-Centric', description: 'We work closely with you to understand your vision and bring it to life with precision.' },
          { title: 'Comprehensive Support', description: 'From initial consultation to post-launch support, we\'re with you every step of the way.' }
        ]
      }
    },
    {
      id: 'webDevelopment',
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      ),
      title: 'Web Development',
      tag: 'Development',
      description: 'Elexyt Technologies excels in web development by combining innovative design principles with cutting-edge technologies to create dynamic, responsive, and user-centric web solutions.',
      modalContent: {
        title: 'Web Development Services',
        tagline: 'Building Robust and Scalable Web Solutions',
        intro: 'At Elexyt Technologies, we offer comprehensive web development services designed to create high-performing, scalable, and secure web applications. Our team of skilled developers uses the latest technologies and best practices to deliver solutions tailored to your business needs.',
        offerings: [
          { title: 'Custom Web Development', description: 'Tailored web solutions that fit your specific requirements and business goals.' },
          { title: 'Front-End Development', description: 'Engaging and responsive user interfaces using HTML, CSS, and JavaScript frameworks.' },
          { title: 'Back-End Development', description: 'Robust server-side solutions using languages like PHP, Python, and Node.js.' },
          { title: 'Content Management Systems (CMS)', description: 'Development and customization of CMS platforms like WordPress, Joomla, and Drupal.' },
          { title: 'API Integration', description: 'Seamless integration with third-party services and APIs to enhance functionality.' },
          { title: 'E-Commerce Development', description: 'Building scalable e-commerce platforms with secure payment gateways and user-friendly features.' },
          { title: 'Maintenance & Support', description: 'Ongoing maintenance and support to ensure your website remains up-to-date and performs optimally.' }
        ],
        whyChoose: [
          { title: 'Experienced Developers', description: 'Our team has extensive experience in developing web applications using a variety of technologies.' },
          { title: 'Innovative Solutions', description: 'We leverage the latest tools and techniques to deliver cutting-edge solutions that drive results.' },
          { title: 'Client-Centric Approach', description: 'We focus on understanding your business objectives to provide solutions that align with your goals.' },
          { title: 'Quality Assurance', description: 'Rigorous testing and quality assurance to ensure your web application functions flawlessly.' },
          { title: 'Timely Delivery', description: 'Commitment to delivering projects on time without compromising on quality.' }
        ]
      }
    },
    {
      id: 'digitalTransformation',
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 7l10 5 10-5-10-5z" />
          <path d="M2 17l10 5 10-5" />
          <path d="M2 12l10 5 10-5" />
        </svg>
      ),
      title: 'Digital Transformation',
      tag: 'Strategy',
      description: 'Empowering organizations to navigate digital transformation by integrating innovative technologies, optimizing processes, and enhancing customer experiences for sustainable growth.',
      modalContent: {
  title: 'Digital Transformation Services',
  tagline: 'Modernizing Your Business for the Digital Age',
  intro: 'At Elexyt Technologies, we guide organizations through comprehensive digital transformation journeys — integrating innovative technologies, optimizing processes, and creating new value streams to ensure sustainable competitive advantage.',
  offerings: [
    { title: 'Technology Assessment', description: 'Evaluating your current technology landscape and identifying opportunities for modernization and improvement.' },
    { title: 'Cloud Migration', description: 'Seamlessly migrating your infrastructure and applications to the cloud for greater scalability and efficiency.' },
    { title: 'Process Automation', description: 'Automating repetitive workflows using AI and RPA tools to reduce costs and improve accuracy.' },
    { title: 'Data & Analytics', description: 'Building data pipelines and dashboards to turn raw information into actionable business insights.' },
    { title: 'Digital Strategy Consulting', description: 'Crafting a clear roadmap aligned with your business goals for a successful digital transition.' }
  ],
  whyChoose: [
    { title: 'Holistic Approach', description: 'We look at people, processes, and technology together — not just the tools.' },
    { title: 'Proven Frameworks', description: 'We use industry-tested transformation frameworks adapted to your business context.' },
    { title: 'Change Management', description: 'We support your team through the transition with training and ongoing guidance.' },
    { title: 'Measurable Outcomes', description: 'Every initiative is tied to clear KPIs so you can track real business impact.' }
  ]
}
    },
    {
      id: 'webSecurity',
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      ),
      title: 'Web Security',
      tag: 'Security',
      description: 'Security is a top priority in Elexyt\'s web development process. They implement industry-standard security practices to protect websites from vulnerabilities and cyber threats.',
      modalContent: {
        title: 'Web Security Services',
        tagline: 'Protecting Your Digital Assets from Threats',
        intro: 'At Elexyt Technologies, we offer comprehensive web security services designed to safeguard your online assets from various cyber threats. Our solutions ensure that your website and data remain protected, maintaining the integrity and confidentiality of your information.',
        offerings: [
          { title: 'Vulnerability Assessment', description: 'Identifying and evaluating potential security vulnerabilities in your web applications and infrastructure.' },
          { title: 'Penetration Testing', description: 'Simulating cyber-attacks to uncover weaknesses and assess the effectiveness of your security defenses.' },
          { title: 'Malware Removal', description: 'Detecting and removing malicious software that could compromise your website\'s security.' },
          { title: 'Firewall Implementation', description: 'Setting up and configuring firewalls to block unauthorized access and protect against attacks.' },
          { title: 'SSL/TLS Encryption', description: 'Implementing SSL/TLS certificates to secure data transmission between your site and its users.' },
          { title: 'Security Monitoring', description: 'Continuous monitoring of your website for suspicious activities and potential threats.' },
          { title: 'Incident Response', description: 'Rapid response and remediation in the event of a security breach or attack.' },
          { title: 'Compliance Assurance', description: 'Ensuring your website meets industry standards and regulatory requirements for data protection.' }
        ],
        whyChoose: [
          { title: 'Expertise', description: 'Our team consists of experienced security professionals who stay updated with the latest threats and solutions.' },
          { title: 'Proactive Approach', description: 'We focus on preventing attacks before they occur by implementing robust security measures.' },
          { title: 'Comprehensive Solutions', description: 'Our services cover all aspects of web security, from assessment to ongoing protection.' },
          { title: 'Customized Security', description: 'Tailored security solutions designed to meet the specific needs of your business.' },
          { title: '24/7 Support', description: 'Providing round-the-clock support to address any security concerns or incidents.' }
        ]
      }
    },
    {
      id: 'digitalMarketing',
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      ),
      title: 'Digital Marketing',
      tag: 'Marketing',
      description: 'Elexyt Technologies\'s digital marketing strategy is designed to help businesses achieve their goals by leveraging a comprehensive and innovative approach to online presence and engagement.',
      modalContent: {
        title: 'Digital Marketing Services',
        tagline: 'Driving Growth Through Strategic Digital Campaigns',
        intro: 'At Elexyt Technologies, we specialize in creating and executing dynamic digital marketing strategies that drive results. Our services are designed to enhance your online presence, engage your target audience, and achieve your business goals.',
        offerings: [
          { title: 'Search Engine Optimization (SEO)', description: 'Improving your website\'s visibility on search engines through keyword optimization, on-page, and off-page strategies.' },
          { title: 'Pay-Per-Click (PPC) Advertising', description: 'Managing targeted ad campaigns on platforms like Google Ads and Bing Ads to drive qualified traffic and conversions.' },
          { title: 'Social Media Marketing', description: 'Creating and managing social media campaigns to build brand awareness and engage with your audience on platforms such as Facebook, Instagram, and LinkedIn.' },
          { title: 'Content Marketing', description: 'Developing and distributing valuable content to attract and retain customers, including blogs, infographics, and videos.' },
          { title: 'Email Marketing', description: 'Crafting and executing email campaigns to nurture leads, drive sales, and build customer loyalty.' },
          { title: 'Influencer Marketing', description: 'Partnering with influencers to amplify your brand message and reach new audiences through authentic endorsements.' },
          { title: 'Analytics & Reporting', description: 'Providing detailed insights and reports on campaign performance, enabling data-driven decisions and strategy adjustments.' }
        ],
        whyChoose: [
          { title: 'Data-Driven Approach', description: 'We use data and analytics to inform our strategies and optimize campaign performance.' },
          { title: 'Customized Strategies', description: 'Tailored digital marketing plans designed to meet the unique needs of your business.' },
          { title: 'Expert Team', description: 'Our team consists of experienced digital marketers with a track record of delivering successful campaigns.' },
          { title: 'Transparent Reporting', description: 'Clear and detailed reporting to keep you informed of progress and results.' },
          { title: 'Results-Oriented', description: 'Focused on delivering measurable results and achieving your business objectives.' }
        ]
      }
    },
    {
      id: 'mobileApp',
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
          <line x1="12" y1="18" x2="12.01" y2="18" />
        </svg>
      ),
      title: 'Mobile Application',
      tag: 'Product',
      description: 'Elexyt Technologies specialize in creating custom mobile applications that drive user engagement and business growth.',
      modalContent: {
        title: 'Mobile App Development Services',
        tagline: 'Building Innovative and High-Performance Mobile Applications',
        intro: 'At Elexyt Technologies, we specialize in creating custom mobile applications that drive user engagement and business growth. Our mobile app development services encompass the entire lifecycle from concept to deployment, ensuring a seamless and effective app experience.',
        offerings: [
          { title: 'Consultation & Strategy', description: 'Understanding your business needs and defining a strategic roadmap for your mobile app.' },
          { title: 'UI/UX Design', description: 'Designing intuitive and engaging user interfaces to provide a superior user experience.' },
          { title: 'App Development', description: 'Building robust and scalable applications for iOS and Android platforms using the latest technologies.' },
          { title: 'Backend Development', description: 'Developing a secure and efficient backend infrastructure to support your mobile app\'s functionality.' },
          { title: 'API Integration', description: 'Integrating third-party services and APIs to enhance your app\'s capabilities and performance.' },
          { title: 'Quality Assurance', description: 'Conducting thorough testing to ensure your app is bug-free and performs flawlessly across all devices.' },
          { title: 'Deployment & Launch', description: 'Managing the app submission process to app stores and ensuring a smooth launch.' },
          { title: 'Maintenance & Support', description: 'Providing ongoing support and updates to keep your app running smoothly and up-to-date.' }
        ],
        whyChoose: [
          { title: 'Expert Development Team', description: 'Our team consists of skilled developers with extensive experience in mobile app development.' },
          { title: 'Cutting-Edge Technologies', description: 'Utilizing the latest technologies and tools to deliver high-performance applications.' },
          { title: 'Custom Solutions', description: 'Tailoring solutions to meet your specific business needs and goals.' },
          { title: 'User-Centric Design', description: 'Focusing on creating apps that are user-friendly and provide an exceptional experience.' },
          { title: 'Timely Delivery', description: 'Committed to delivering projects on time without compromising on quality.' }
        ]
      }
    }
  ];

  const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

  const openModal = (serviceId) => {
    setActiveModal(serviceId);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setActiveModal(null);
    document.body.style.overflow = 'unset';
  };

  return (
    <>
    <div className="scroll-progress" style={{ width: `${scrollProgress}%` }} />
    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="services-hero-background" ref={heroRef}>
          <div className="services-hero-pattern"></div>
          <div className="services-hero-gradient"></div>
        </div>

        <div className="container">
          <div className="services-hero-content">
            <div className="hero-badge-wrapper">
              <span className="hero-badge">
                <span className="badge-dot"></span>
                Professional Services
              </span>
            </div>

            <h1 className="services-hero-title">
              Services Built Specifically
              <span className="hero-title-gradient"> For Your Business</span>
            </h1>

            <p className="services-hero-description">
              From concept to deployment, we deliver comprehensive digital solutions that drive measurable results and sustainable growth for your business.
            </p>
          </div>
        </div>

        {/* Stats Section */}
        {/* <div className="services-stats">
          <div className="container">
            <div className="stats-wrapper">
              {stats.map((stat, index) => (
                <div key={index} className="stat-card">
                  <div className="stat-icon">{stat.icon}</div>
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div> */}
      </section>

    
      {/* Services Section */}
      <section className="service-page-main-section">
        <div className="container">
          <div className="section-header centered">
            <div className="section-label">
              <span className="label-line"></span>
              <span>Our Expertise</span>
              <span className="label-line"></span>
            </div>
            <h2 className="section-title-large">
              Comprehensive Digital Solutions
            </h2>
            <p className="section-subtitle">
              Expert services designed to transform your business and accelerate digital growth
            </p>
          </div>

          <div className="service-page-grid">
            {services.map((service, index) => (
              <div key={index} className="service-page-card">
                <div className="service-page-card-inner">
                  <div className="service-page-header">
                    <div className="service-page-icon-wrapper">
                      <div className="service-page-icon">{service.icon}</div>
                    </div>
                    <span className="service-page-tag">{service.tag}</span>
                  </div>

                  <h3 className="service-page-title">{service.title}</h3>
                  <p className="service-page-description">{service.description}</p>

                  {service.modalContent ? (
                    <button
                      onClick={() => openModal(service.id)}
                      className="service-page-link"
                    >
                      <span>Learn More</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </button>
                  ) : (
                    <a href="/services" className="service-page-link">
                      <span>Learn More</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </a>
                  )}
                </div>
                <div className="service-page-card-glow"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta-section">
        <div className="cta-pattern"></div>
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">
              Ready to Transform Your Business?
            </h2>
            <p className="cta-description">
              Let's discuss how our services can help you achieve your digital goals. Our expert team is ready to deliver exceptional results.
            </p>
            <div className="cta-actions">
              <a href="/contact" className="btn btn-light btn-large">
                Get Started Today
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </a>
              <a href="/project" className="btn btn-outline-light-alt btn-large">
                View Our Work
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Modals */}
      {services.map((service) =>
        service.modalContent && activeModal === service.id ? (
          <div key={service.id} className="modal-overlay" onClick={closeModal}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={closeModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>

              <div className="modal-header">
                <div className="modal-icon-wrapper">
                  {service.icon}
                </div>
                <h2>{service.modalContent.title}</h2>
                <p>{service.modalContent.tagline}</p>
              </div>

              <div className="modal-body">
                <p className="modal-intro">{service.modalContent.intro}</p>

                <div className="modal-section">
                  <h3>What We Offer</h3>
                  <div className="modal-cards-grid">
                    {service.modalContent.offerings.map((item, idx) => (
                      <div key={idx} className="modal-service-card">
                        <div className="modal-card-number">{String(idx + 1).padStart(2, '0')}</div>
                        <h4>{item.title}</h4>
                        <p>{item.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="modal-section">
                  <h3>Why Choose Elexyt?</h3>
                  <div className="modal-features-grid">
                    {service.modalContent.whyChoose.map((item, idx) => (
                      <div key={idx} className="modal-feature-card">
                        <div className="feature-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </div>
                        <div className="feature-content">
                          <h4>{item.title}</h4>
                          <p>{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="modal-cta">
                  <a href="/contact" className="btn btn-primary btn-large">
                    Start Your Project
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        ) : null
      )}
    </div>

     <button
      className={`back-to-top ${showBackToTop ? 'visible' : ''}`}
      onClick={scrollToTop}
      aria-label="Scroll to top"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="18 15 12 9 6 15" />
      </svg>
    </button>
<MobileNav />
    </>
  );
};

export default Services;